
import { NestedFilter } from '@@partner:types:graphql'

import React, { useCallback, useState } from 'react'

import { useFetchAllAssemblers } from '@@partner:graphql/query/employees/allAssemblers.gql'

import { formatPhone } from '@@partner:helpers/util'
import { useGridHelper } from '@@partner:helpers/crud/grid'

import { Link, generatePath } from 'react-router-dom'
import { Tag, Pagination, FlexboxGrid, SelectPicker, ButtonGroup, Button } from 'rsuite'
import CommonStatusLabel from '@components/common/CommonStatusLabel'
import { EditButton } from '@components/common/form/Buttons'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import ResponsiveTable, { ResponsiveTableColumn } from '@components/common/ResponsiveTable/ResponsiveTable'

import { ROUTE_USERS_MANAGEMENT_EDIT, ROUTE_USERS_MANAGEMENT_VIEW_ASSEMBLER } from '@@partner:config/routes'
import { Assembler } from '@@partner:types:app'
import AssemblersFilter from './AssemblersFilter'

export interface AssemblersTableProps {
  header?: string
}

const columns: ResponsiveTableColumn<Assembler>[] = [
  {
    dataKey: 'id',
    header: 'ИД',
    sortable: true,
    flexGrow: 0,
    width: 80,
    render: (assembler) => (
      <Link to={generatePath(ROUTE_USERS_MANAGEMENT_VIEW_ASSEMBLER, { id: assembler.id })}>
        {assembler.id}
      </Link>
    )
  },
  {
    dataKey: 'first_name',
    header: 'ФИО',
    sortable: true,
    flexGrow: 2,
    isCardTitle: true,
    render: (assembler) => <span>{assembler.fullName}</span>
  },
  {
    dataKey: 'phone',
    header: 'Телефон',
    sortable: true,
    flexGrow: 2,
    render: (assembler) => <Tag color="blue">{formatPhone(assembler.phone)}</Tag>
  },
  {
    dataKey: 'status',
    header: 'Статус',
    sortable: true,
    isCardBadge: true,
    render: (assembler) => <CommonStatusLabel status={assembler.status} />
  },
  {
    dataKey: 'shop_id',
    header: 'Магазин',
    sortable: true,
    flexGrow: 2,
    render: (assembler) => <Tag>{assembler?.shop?.name}</Tag>
  }
]

export default function AssemblersTable ({ header }: AssemblersTableProps) {
  const { page, tableSort, limit, defaultLimitOptions, handleLimitSelect, handlePageSelect, handleSortColumn } =
    useGridHelper()
  
  const [ filter, setFilter ] = useState<NestedFilter>({
    fields: [ 'id' ],
    op: 'unwrap'
  })

  const { data, loading, error } = useFetchAllAssemblers({
    filter,
    pager: { page },
    sort: {
      direction: tableSort.direction,
      fieldName: tableSort.fieldName
    }
  })

  const handleFilterChanged = useCallback((updatedFilter: NestedFilter) => {
    setFilter(updatedFilter)
    handlePageSelect(1)
  }, [ handlePageSelect ])

  const renderActions = useCallback((assembler: Assembler) => (
    <ButtonGroup size="xs">
      <Link to={generatePath(ROUTE_USERS_MANAGEMENT_VIEW_ASSEMBLER, { id: assembler.id })}>
        <Button appearance="ghost">Просмотр</Button>
      </Link>
      <Link to={generatePath(ROUTE_USERS_MANAGEMENT_EDIT, { type: 'assemblers', id: assembler.id })}>
        <EditButton>Изменить</EditButton>
      </Link>
    </ButtonGroup>
  ), [])

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        error={error}
        title="Ошибка загрузки сборщиков"
      />
    )
  }

  const assemblers = data?.assemblers?.edges ?? []
  const pageInfo = data?.assemblers?.pageInfo

  return (
    <>
      {header && <h4 className="u-form-heading">{header}</h4>}
      <AssemblersFilter onFilterChanged={handleFilterChanged} />
      <ResponsiveTable
        data={assemblers}
        columns={columns}
        loading={loading}
        sortColumn={tableSort.fieldName}
        sortType={tableSort.sortType}
        onSortColumn={handleSortColumn}
        renderActions={renderActions}
      />
      <FlexboxGrid
        justify="space-between"
        align="middle"
        style={{ marginTop: 10 }}
      >
        <Pagination
          last
          first
          next
          prev
          ellipsis
          activePage={page}
          pages={pageInfo?.lastPage}
          total={pageInfo?.total ?? 0}
          maxButtons={10}
          onSelect={handlePageSelect}
        />
        <SelectPicker
          value={limit}
          data={defaultLimitOptions.map((key) => ({ value: key, label: key }))}
          cleanable={false}
          searchable={false}
          onChange={handleLimitSelect}
        />
      </FlexboxGrid>
    </>
  )
}
